import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Play, ExternalLink } from 'lucide-react';

const Speaking = () => {
  const [playingVideo, setPlayingVideo] = useState(null);
  
  const harvardVideoRef = useRef(null);
  const oklahomaVideoRef = useRef(null);

  const handlePlayVideo = (videoId, videoRef) => {
    if (playingVideo === videoId) {
      videoRef.current?.pause();
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
      videoRef.current?.play();
    }
  };

  // Assets
  const assets = {
    harvardVideo: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/c0oicwc4_VIDEO-2026-02-09-14-29-17.mp4",
    oklahomaVideo: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/ohxtsaj8_VIDEO-2026-02-09-14-31-40.mp4",
    arabCenterPhoto: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/6k9gjlj8_PHOTO-2026-02-09-14-25-55.jpg",
    harvardScreenshot: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/tfjhikg8_Screen%20Shot%202026-02-09%20at%202.48.55%20PM.png",
    powerToFlyScreenshot: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/d4urm4lt_Screen%20Shot%202026-02-09%20at%202.47.38%20PM.png"
  };

  const mediaLinks = [
    {
      platform: "Course Report",
      title: "Tech Interviews in the Age of AI",
      type: "Live Q&A",
      url: "https://www.youtube.com/watch?v=MJERqJ2HWZw"
    },
    {
      platform: "Course Report",
      title: "How to Break Into Cybersecurity",
      type: "Article",
      url: "https://www.coursereport.com/blog/live-q-a-how-to-break-into-cybersecurity-in-2023"
    },
    {
      platform: "Course Report",
      title: "Featured on X (Twitter)",
      type: "Social",
      url: "https://x.com/CourseReport/status/1927772058630406595"
    },
    {
      platform: "PowerToFly",
      title: "How to Advance Your Career After a Bootcamp",
      type: "Event",
      url: "https://powertofly.com/events/details/55565-how-to-advance-your-career-after-a-bootcamp/"
    },
    {
      platform: "YouTube",
      title: "AI & Career Fluency",
      type: "Video",
      url: "https://youtu.be/0W1MJAI7Ivc"
    }
  ];

  const focusAreas = [
    "AI & the future of work",
    "Career mobility and international relocation",
    "Arab identity, culture, and preservation",
    "Technology as a cultural tool",
    "Education, infrastructure, and access"
  ];

  const availableFor = [
    "Keynotes & panels",
    "Universities & institutions",
    "Cultural organizations",
    "Technology conferences",
    "Private salons & advisory sessions"
  ];

  return (
    <div className="bg-black pt-24 lg:pt-32">
      {/* Hero Section */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
            Speaking & Media
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white font-normal leading-[0.95] tracking-tight max-w-4xl">
            Speaking & Media
          </h1>
          <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8 max-w-2xl">
            I speak globally on culture, technology, identity, and the future of work — translating complex ideas into accessible, actionable conversations.
          </p>
        </div>
      </section>

      {/* Selected Speaking Engagements */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
              Live Events
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
              Selected Speaking Engagements
            </h2>
          </div>

          {/* Harvard University */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20">
            <div className="relative aspect-video bg-neutral-900 overflow-hidden">
              <video
                ref={harvardVideoRef}
                src={assets.harvardVideo}
                className="w-full h-full object-cover"
                playsInline
                onClick={() => handlePlayVideo('harvard', harvardVideoRef)}
              />
              {playingVideo !== 'harvard' && (
                <button
                  onClick={() => handlePlayVideo('harvard', harvardVideoRef)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                >
                  <div className="w-20 h-20 rounded-full border-2 border-white flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <Play size={32} className="text-white ml-1" fill="currentColor" />
                  </div>
                </button>
              )}
            </div>
            <div className="flex flex-col justify-center">
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mb-2">
                Harvard University
              </p>
              <h3 className="font-serif text-2xl lg:text-3xl text-white tracking-tight">
                The National Arab Institute
              </h3>
              <p className="font-sans text-sm text-white/50 mt-4">
                Topic: Preserving Arab Culture Through Technology & Institutions
              </p>
            </div>
          </div>

          {/* Oklahoma State University */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20">
            <div className="lg:order-2 relative aspect-video bg-neutral-900 overflow-hidden">
              <video
                ref={oklahomaVideoRef}
                src={assets.oklahomaVideo}
                className="w-full h-full object-cover"
                playsInline
                onClick={() => handlePlayVideo('oklahoma', oklahomaVideoRef)}
              />
              {playingVideo !== 'oklahoma' && (
                <button
                  onClick={() => handlePlayVideo('oklahoma', oklahomaVideoRef)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                >
                  <div className="w-20 h-20 rounded-full border-2 border-white flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <Play size={32} className="text-white ml-1" fill="currentColor" />
                  </div>
                </button>
              )}
            </div>
            <div className="lg:order-1 flex flex-col justify-center">
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mb-2">
                Oklahoma State University
              </p>
              <h3 className="font-serif text-2xl lg:text-3xl text-white tracking-tight">
                Arab Heritage Week Fundraiser Dinner
              </h3>
              <p className="font-sans text-sm text-white/50 mt-4">
                Topic: The Importance of Preserving Arab Culture
              </p>
            </div>
          </div>

          {/* Arab American Community Center */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="aspect-video overflow-hidden">
              <img
                src={assets.arabCenterPhoto}
                alt="Arab American Community Center speaking event"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center">
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mb-2">
                Arab American Community Center
              </p>
              <h3 className="font-serif text-2xl lg:text-3xl text-white tracking-tight">
                Cultural Preservation, Identity, and Community Memory
              </h3>
              <p className="font-sans text-sm text-white/50 mt-4">
                Speaking on the importance of preserving Arab heritage and building community through shared cultural experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Career Media */}
      <section className="py-14 md:py-24 lg:py-24 bg-[#F8F6F3]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
              Featured In
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-black font-normal tracking-tight">
              Technology & Career Media
            </h2>
            <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-6 max-w-2xl">
              I'm regularly featured in conversations about technology careers, AI fluency, and global workforce mobility.
            </p>
          </div>

          {/* Media Screenshots */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="overflow-hidden">
              <img
                src={assets.harvardScreenshot}
                alt="Harvard Conference feature"
                className="w-full h-auto"
              />
            </div>
            <div className="overflow-hidden">
              <img
                src={assets.powerToFlyScreenshot}
                alt="PowerToFly feature"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Media Links */}
          <div className="space-y-0 mt-12">
            {mediaLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group py-6 border-t border-black/10 first:border-t-0 flex flex-col md:flex-row md:items-center md:justify-between gap-2 hover:bg-black/5 px-4 -mx-4 transition-colors"
              >
                <div>
                  <p className="font-sans text-xs text-black/40 uppercase tracking-wider mb-1">
                    {link.platform} · {link.type}
                  </p>
                  <h3 className="font-serif text-xl text-black tracking-tight group-hover:text-[#6B0F1A] transition-colors">
                    {link.title}
                  </h3>
                </div>
                <ExternalLink size={18} className="text-black/30 group-hover:text-[#6B0F1A] transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Media Focus Areas & Available For */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Focus Areas */}
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Topics
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-normal tracking-tight">
                Media Focus Areas
              </h2>
              <div className="mt-8 space-y-0">
                {focusAreas.map((area, index) => (
                  <div key={index} className="py-4 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/70">{area}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Available For */}
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Engagements
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-normal tracking-tight">
                Available For
              </h2>
              <div className="mt-8 space-y-0">
                {availableFor.map((item, index) => (
                  <div key={index} className="py-4 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/70">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
              Let's Start a Conversation
            </h2>
            <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
              If you're building something that sits at the intersection of culture, technology, or global impact — I'd love to hear from you.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center mt-12 px-12 py-5 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all duration-300"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Speaking;
